import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  TrendingUp,
  Plus,
  Euro,
  User,
  MoreHorizontal,
  Building2,
  LayoutGrid,
  List,
  Home,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { trpc } from "@/lib/trpc";

// ─── Types ───────────────────────────────────────────────────────────────────

export type LeadStatus = "PROSPECT" | "DEMO" | "NEGOCIATION" | "GAGNE" | "PERDU";
export type LeadSource = "INBOUND" | "OUTBOUND" | "TRAVAUX_BTP" | "REFERRAL" | "OTHER";

export interface Lead {
  id: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  status: LeadStatus;
  score: number;
  source: LeadSource;
  estimatedValue: number;
  notes: string;
  assignee: string;
  createdAt: string;
  lastActivity: string;
}

// ─── Config colonnes Kanban ───────────────────────────────────────────────────

const COLUMNS: { status: LeadStatus; label: string; color: string; bgColor: string }[] = [
  { status: "PROSPECT", label: "Prospect", color: "text-primary", bgColor: "bg-primary/5" },
  { status: "DEMO", label: "Démo", color: "text-pipeline-demo", bgColor: "bg-pipeline-demo/10" },
  { status: "NEGOCIATION", label: "Négociation", color: "text-warning", bgColor: "bg-warning/5" },
  { status: "GAGNE", label: "Gagné", color: "text-success", bgColor: "bg-success/5" },
  { status: "PERDU", label: "Perdu", color: "text-destructive", bgColor: "bg-destructive/5" },
];

const SOURCE_LABELS: Record<LeadSource, string> = {
  INBOUND: "Entrant",
  OUTBOUND: "Sortant",
  TRAVAUX_BTP: "TravauxBTP",
  REFERRAL: "Référence",
  OTHER: "Autre",
};

// ─── Components ───────────────────────────────────────────────────────────────

function ScoreBadge({ score }: { score: number }) {
  const color =
    score >= 80
      ? "bg-success/10 text-success"
      : score >= 60
      ? "bg-warning/10 text-warning"
      : "bg-destructive/10 text-destructive";
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${color}`}>
      {score}/100
    </span>
  );
}

function LeadCard({
  lead,
  onMove,
}: {
  lead: Lead;
  onMove: (id: string, status: LeadStatus) => void;
}) {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const nextStatuses = COLUMNS.map((c) => c.status).filter((s) => s !== lead.status);

  return (
    <div
      className="bg-white rounded-lg border border-border p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
      onClick={() => navigate(`/sales/leads/${lead.id}`)}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="font-medium text-sm truncate">{lead.name}</div>
          <div className="text-xs text-muted-foreground flex items-center gap-1 truncate">
            <Building2 aria-hidden="true" className="h-3 w-3 shrink-0" />
            {lead.company}
          </div>
        </div>
        <div className="relative shrink-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
            aria-label="Options du lead"
            aria-expanded={showMenu}
            className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-muted transition-opacity"
          >
            <MoreHorizontal aria-hidden="true" className="h-3 w-3" />
          </button>
          {showMenu && (
            <div className="absolute right-0 top-6 z-10 bg-white border rounded-lg shadow-lg py-1 min-w-[140px]">
              {nextStatuses.map((s) => {
                const col = COLUMNS.find((c) => c.status === s)!;
                return (
                  <button
                    key={s}
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMove(lead.id, s);
                      setShowMenu(false);
                    }}
                  >
                    → {col.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-2">
        <ScoreBadge score={lead.score} />
        <span className="text-xs text-muted-foreground">{SOURCE_LABELS[lead.source]}</span>
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <Euro aria-hidden="true" className="h-3 w-3" />
          {lead.estimatedValue.toLocaleString("fr-FR")} €/an
        </span>
        <span className="flex items-center gap-1">
          <User aria-hidden="true" className="h-3 w-3" />
          {lead.assignee || "—"}
        </span>
      </div>
    </div>
  );
}

function KanbanColumn({
  config,
  leads,
  onMove,
  onAddLead,
  canAdd,
}: {
  config: (typeof COLUMNS)[0];
  leads: Lead[];
  onMove: (id: string, status: LeadStatus) => void;
  onAddLead: (status: LeadStatus) => void;
  canAdd: boolean;
}) {
  const totalValue = leads.reduce((sum, l) => sum + l.estimatedValue, 0);

  return (
    <div role="region" aria-label={`Colonne ${config.label}`} className="flex flex-col min-w-[240px] w-64 shrink-0">
      <div className={`rounded-t-lg px-3 py-2 ${config.bgColor}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`font-semibold text-sm ${config.color}`}>{config.label}</span>
            <span className="bg-white rounded-full px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
              {leads.length}
            </span>
          </div>
          {canAdd && config.status !== "GAGNE" && config.status !== "PERDU" && (
            <button
              onClick={() => onAddLead(config.status)}
              aria-label={`Ajouter un lead ${config.label}`}
              className={`p-1 rounded hover:bg-white/50 transition-colors ${config.color}`}
            >
              <Plus aria-hidden="true" className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {leads.length > 0 && (
          <div className={`text-xs mt-0.5 ${config.color} opacity-70`}>
            {totalValue.toLocaleString("fr-FR")} €/an
          </div>
        )}
      </div>

      <div className="flex-1 bg-muted/30 rounded-b-lg p-2 space-y-2 min-h-[200px]">
        {leads.map((lead) => (
          <LeadCard key={lead.id} lead={lead} onMove={onMove} />
        ))}
        {leads.length === 0 && (
          <div className="text-center text-xs text-muted-foreground py-8">Aucun lead</div>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SalesPipeline() {
  const { user } = useAuth();
  const canAdd = canWrite(user?.staffRole);

  const [sourceFilter, setSourceFilter] = useState<"ALL" | "TRAVAUX_BTP">("ALL");
  const { data: leadsData, isLoading, refetch } = trpc.sales.leads.list.useQuery(
    sourceFilter !== "ALL" ? { source: sourceFilter } : undefined
  );
  const updateMutation = trpc.sales.leads.update.useMutation({
    onSuccess: () => {
      toast.success("Lead déplacé");
      refetch();
    },
    onError: () => toast.error("Erreur lors du déplacement"),
  });
  const createMutation = trpc.sales.leads.create.useMutation({
    onSuccess: () => {
      toast.success("Lead créé");
      setShowCreateDialog(false);
      setForm({ name: "", company: "", email: "", phone: "", source: "INBOUND", estimatedValue: "", notes: "" });
      refetch();
    },
    onError: () => toast.error("Erreur lors de la création"),
  });

  const [viewMode, setViewMode] = useState<"kanban" | "list">("kanban");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newLeadStatus, setNewLeadStatus] = useState<LeadStatus>("PROSPECT");
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    source: "INBOUND" as LeadSource,
    estimatedValue: "",
    notes: "",
  });

  const leads = (leadsData ?? []) as Lead[];

  const leadsPerColumn = (status: LeadStatus) => leads.filter((l) => l.status === status);

  const totalPipeline = leads
    .filter((l) => l.status !== "PERDU")
    .reduce((sum, l) => sum + l.estimatedValue, 0);
  const wonLeads = leads.filter((l) => l.status === "GAGNE");
  const conversionRate = leads.length > 0 ? Math.round((wonLeads.length / leads.length) * 100) : 0;

  function handleMove(id: string, status: LeadStatus) {
    updateMutation.mutate({ id, status });
  }

  function handleAddLead(status: LeadStatus) {
    setNewLeadStatus(status);
    setShowCreateDialog(true);
  }

  function handleCreateLead() {
    if (!form.name || !form.company || !form.email) {
      toast.error("Nom, entreprise et email sont requis");
      return;
    }
    createMutation.mutate({
      name: form.name,
      company: form.company,
      email: form.email,
      phone: form.phone,
      source: form.source,
      estimatedValue: Number(form.estimatedValue) || 0,
      notes: form.notes,
      status: newLeadStatus,
    });
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <TrendingUp className="h-6 w-6 text-primary" />
            Pipeline Sales
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Gérez vos prospects depuis le premier contact jusqu'à la conversion
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Source filter */}
          <button
            type="button"
            onClick={() => setSourceFilter((s) => s === "TRAVAUX_BTP" ? "ALL" : "TRAVAUX_BTP")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm transition-colors ${
              sourceFilter === "TRAVAUX_BTP"
                ? "bg-warning/10 border-warning/30 text-warning font-medium"
                : "text-muted-foreground hover:bg-muted"
            }`}
            title="Filtrer par origine TravauxBTP"
          >
            <Home className="h-3.5 w-3.5" />
            TravauxBTP
          </button>

          {/* View mode toggle */}
          <div className="flex rounded-lg border overflow-hidden">
            <button
              type="button"
              className={`px-2.5 py-1.5 text-sm transition-colors ${viewMode === "kanban" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
              onClick={() => setViewMode("kanban")}
              title="Vue Kanban"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={`px-2.5 py-1.5 text-sm transition-colors ${viewMode === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
              onClick={() => setViewMode("list")}
              title="Vue Liste"
            >
              <List className="h-4 w-4" />
            </button>
          </div>
          {canAdd && (
            <Button onClick={() => handleAddLead("PROSPECT")}>
              <Plus className="h-4 w-4 mr-2" />
              Nouveau lead
            </Button>
          )}
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="text-xs text-muted-foreground mb-1">Total pipeline</div>
            <div className="text-xl font-bold">{totalPipeline.toLocaleString("fr-FR")} €</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="text-xs text-muted-foreground mb-1">Leads actifs</div>
            <div className="text-xl font-bold">
              {leads.filter((l) => !["GAGNE", "PERDU"].includes(l.status)).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="text-xs text-muted-foreground mb-1">Taux conversion</div>
            <div className="text-xl font-bold">{conversionRate}%</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="text-xs text-muted-foreground mb-1">Gagnés</div>
            <div className="text-xl font-bold">{wonLeads.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Kanban Board */}
      {viewMode === "kanban" && (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-4 min-w-max">
            {COLUMNS.map((col) => (
              <KanbanColumn
                key={col.status}
                config={col}
                leads={isLoading ? [] : leadsPerColumn(col.status)}
                onMove={handleMove}
                onAddLead={handleAddLead}
                canAdd={canAdd && !isLoading}
              />
            ))}
          </div>
        </div>
      )}

      {/* List View */}
      {viewMode === "list" && (
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="py-12 text-center text-muted-foreground text-sm">Chargement…</div>
            ) : leads.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground text-sm">Aucun lead</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Contact</TableHead>
                    <TableHead>Entreprise</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Valeur</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Assigné</TableHead>
                    <TableHead>Dernière activité</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((lead) => {
                    const col = COLUMNS.find((c) => c.status === lead.status);
                    return (
                      <TableRow
                        key={lead.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => navigate(`/sales/leads/${lead.id}`)}
                      >
                        <TableCell>
                          <div className="font-medium text-sm">{lead.name}</div>
                          <div className="text-xs text-muted-foreground">{lead.email}</div>
                        </TableCell>
                        <TableCell className="text-sm">{lead.company}</TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={`text-xs ${col?.color ?? ""}`}
                          >
                            {col?.label ?? lead.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <ScoreBadge score={lead.score} />
                        </TableCell>
                        <TableCell className="text-sm">
                          {lead.estimatedValue.toLocaleString("fr-FR")} €/an
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {SOURCE_LABELS[lead.source]}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {lead.assignee || "—"}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {lead.lastActivity
                            ? new Date(lead.lastActivity).toLocaleDateString("fr-FR")
                            : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create Lead Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nouveau lead</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Nom *</Label>
                <Input
                  placeholder="Jean Dupont"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-xs">Entreprise *</Label>
                <Input
                  placeholder="BTP Pro"
                  value={form.company}
                  onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Email *</Label>
              <Input
                type="email"
                placeholder="contact@btp-pro.fr"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Téléphone</Label>
                <Input
                  placeholder="06 12 34 56 78"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-xs">Valeur estimée (€/an)</Label>
                <Input
                  type="number"
                  placeholder="2400"
                  value={form.estimatedValue}
                  onChange={(e) => setForm((f) => ({ ...f, estimatedValue: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Source</Label>
              <Select
                value={form.source}
                onValueChange={(v) => setForm((f) => ({ ...f, source: v as LeadSource }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SOURCE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea
                placeholder="Contexte, besoins..."
                rows={3}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateLead} disabled={createMutation.isPending}>
              {createMutation.isPending ? "Création…" : "Créer le lead"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
